import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronRight } from "lucide-react";
import { Icon } from "../icons.js";
/**
 * An answer's sources (knowledge-page design-spec §2, §2.1, §4.4; issue 282): a "Sources (n)"
 * toggle, `<button aria-expanded>` in 13/500 ink-2 with a chevron that turns 90deg open, and
 * under it, only while open, the numbered rows over a `--line` rule. Collapsed by default is the
 * caller's (`open`); the toggle is never unmounted, so focus stays on it. Each row is the
 * number in its own cell, then a new-tab `<a data-source-link>` in accent ink reading the text
 * and "↗", underlined on hover (`--source-link-line`, `interactions.css`); a row whose
 * recording is gone is plain ink-2 text (issue 285). Text renders as text. The toggle never
 * reaches the accent (`accent.test.ts`).
 */
export function SourcesList({ id, sources, open, onToggle }) {
    return (_jsxs("div", { "data-sources": "", style: { display: "flex", flexDirection: "column" }, children: [_jsxs("button", { type: "button", "data-sources-toggle": "", "aria-expanded": open, "aria-controls": open ? id : undefined, onClick: () => onToggle(), style: {
                    alignSelf: "flex-start",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "var(--space-05)",
                    // knowledge-page design-spec §4.4: 26px tall, `padding: 0 6px 0 2px; margin-left: -2px`.
                    height: "26px",
                    padding: "0 var(--space-075) 0 var(--space-025)",
                    margin: "0 0 0 calc(-1 * var(--space-025))",
                    border: "none",
                    borderRadius: "var(--radius-control)",
                    background: "var(--sources-toggle-bg, transparent)",
                    color: "var(--sources-toggle-ink, var(--ink-2))",
                    fontFamily: "var(--font-sans)",
                    // knowledge-page design-spec §3.3: 13px Sources toggle size.
                    fontSize: "13px",
                    fontWeight: "var(--type-body-weight-strong)",
                    cursor: "pointer",
                    transitionProperty: "background-color, color",
                    transitionDuration: "var(--dur-fast)",
                    transitionTimingFunction: "var(--ease-out)",
                }, children: [_jsx("span", { "aria-hidden": "true", style: {
                            display: "inline-flex",
                            transform: open ? "rotate(90deg)" : undefined,
                            transitionProperty: "transform",
                            transitionDuration: "var(--dur-fast)",
                            transitionTimingFunction: "var(--ease-out)",
                        }, children: _jsx(Icon, { icon: ChevronRight, size: 14, "aria-hidden": "true" }) }), `Sources (${sources.length})`] }), open && (_jsx("ol", { id: id, "data-sources-list": "", style: {
                    listStyle: "none",
                    // knowledge-page design-spec §4.4: `margin-top: -4px`, a rule, `padding-top: 4px`.
                    margin: "calc(-1 * var(--space-05)) 0 0",
                    padding: "var(--space-05) 0 0",
                    borderTop: "var(--border-hairline) solid var(--line)",
                }, children: sources.map((source) => (_jsxs("li", { "data-source": source.n, style: {
                        display: "grid",
                        // knowledge-page design-spec §4.4: a 20px number column, 28px lines.
                        gridTemplateColumns: "20px 1fr",
                        alignItems: "center",
                        fontSize: "var(--type-small-size)",
                        lineHeight: "28px",
                    }, children: [_jsx("span", { "data-source-n": "", style: { color: "var(--ink-2)", fontVariantNumeric: "tabular-nums" }, children: source.n }), source.href === null ? (_jsx("span", { "data-source-gone": "", style: { color: "var(--ink-2)" }, children: source.text })) : (_jsx("a", { href: source.href, target: "_blank", rel: "noopener noreferrer", "data-source-link": "", "aria-label": source.label, title: source.label, style: {
                                justifySelf: "start",
                                padding: "0 var(--space-05)",
                                marginLeft: "calc(-1 * var(--space-05))",
                                borderRadius: "var(--radius-focus)",
                                color: "var(--accent)",
                                fontVariantNumeric: "tabular-nums",
                                textDecorationLine: "var(--source-link-line, none)",
                                // knowledge-page design-spec §4.4: underline offset 3 on hover.
                                textUnderlineOffset: "3px",
                            }, children: `${source.text} ↗` }))] }, source.n))) }))] }));
}
